import { createContext, useContext, useEffect, useState } from 'react';
import {
    CheckboxFilterConfig,
    DateFilterConfig,
    DateOption,
    FilterConfig,
    Filters,
    NumericFilterConfig,
    NumericOption,
    SelectFilterConfig,
    ToggleFilterConfig,
} from './types';

export type FilterInstance = {
    activeSectionCount: number;
    getFilterSection: <T>(value: string) => T;
    updateCheckboxOption: (id: string, optionValue: string, active: boolean) => void;
    updateNumericOption: (id: string, { base, condition, to }: NumericOption) => void;
    updateDateOption: (id: string, { base, condition, to }: DateOption) => void;
    updateSelectOption: (id: string, activeValues: string[]) => void;
    updateToggleOption: (id: string, active: boolean) => void;
    getActiveFilterCount: (sectionValue: string) => number;
    reset(): void;
    apply: () => void;
    clear: () => void;
    allClosed: boolean;
    resetAllClosed: () => void;
    result: { queryString: string; currentFilterState: Filters };
};

export const FilterContext = createContext<FilterInstance | undefined>(undefined);

export function useFilterContext(): FilterInstance {
    const context = useContext(FilterContext);
    if (context === undefined) {
        throw new Error('useFilter must be used within a FilterProvider');
    }
    return context;
}

export function useFilter(filterOptions: Filters, enableV2Query = false): FilterInstance {
    const [activeSectionCount, setActiveSectionCount] = useState(calcActiveFilterSections(filterOptions));
    const [filters, setFilters] = useState({
        prev: filterOptions,
        current: filterOptions,
        queryString: buildActiveFiltersQueryString(filterOptions),
    });
    const [allClosed, setAllClosed] = useState(false);

    useEffect(() => {
        setActiveSectionCount(calcActiveFilterSections(filters.current));
    }, [filters.current]);

    useEffect(() => {
        setFilters({
            prev: filterOptions,
            current: filterOptions,
            queryString: buildActiveFiltersQueryString(filterOptions),
        });
        setActiveSectionCount(calcActiveFilterSections(filterOptions));
    }, [filterOptions]);

    function getFilterSection<T>(id: string): T {
        return filters.current.find((f) => f.id === id) as unknown as T;
    }

    function updateCheckboxOption(id: string, optionValue: string, active: boolean): void {
        const checkboxSection = getFilterSection<CheckboxFilterConfig>(id);
        if (!checkboxSection) {
            return;
        }

        const checkboxConfig: CheckboxFilterConfig = {
            ...checkboxSection,
            options: checkboxSection.options.map((option) => {
                if (option.value === optionValue) {
                    return { ...option, active };
                }
                return { ...option };
            }),
        };

        setFilters((f) => {
            return {
                ...f,
                current: [...f.current].map((curr) => (curr.id === id ? checkboxConfig : curr)),
            };
        });
    }

    function updateNumericOption(id: string, { base, condition, to }: NumericOption): void {
        const filter = getFilterSection<NumericFilterConfig>(id);
        const updatedFilter = { ...filter, option: { ...filter.option } };

        // if (isNumber(base) || base === '') {
        //     updatedFilter.option.base = base === '' ? undefined : +base;
        // }

        // if (isNumber(to) || base === '') {
        //     updatedFilter.option.to = to === '' ? undefined : +to;
        // }

        updatedFilter.option.base = base;
        updatedFilter.option.to = to;
        updatedFilter.option.condition = condition || '=';

        setFilters((f) => {
            return {
                ...f,
                current: [...f.current].map((curr) => (curr.id === id ? updatedFilter : curr)),
            };
        });
    }

    function updateDateOption(id: string, { base, condition, to }: DateOption): void {
        const filter = getFilterSection<DateFilterConfig>(id);
        const updatedFilter = { ...filter, option: { ...filter.option } };

        // if (isNumber(base) || base === '') {
        //     updatedFilter.option.base = base === '' ? undefined : +base;
        // }

        // if (isNumber(to) || base === '') {
        //     updatedFilter.option.to = to === '' ? undefined : +to;
        // }

        updatedFilter.option.base = base;
        updatedFilter.option.to = to;
        updatedFilter.option.condition = condition || '=';

        setFilters((f) => {
            return {
                ...f,
                current: [...f.current].map((curr) => (curr.id === id ? updatedFilter : curr)),
            };
        });
    }

    function updateSelectOption(id: string, activeValues: string[]): void {
        const filter = getFilterSection<SelectFilterConfig>(id);

        const options = filter.options.map((option) => {
            if (activeValues.find((v) => v === option.value)) {
                return { ...option, active: true };
            }
            return { ...option, active: false };
        });

        const updatedFilter = { ...filter, options };

        setFilters((f) => {
            return {
                ...f,
                current: [...f.current].map((curr) => (curr.id === id ? updatedFilter : curr)),
            };
        });
    }

    function updateToggleOption(id: string, active: boolean): void {
        const filter = getFilterSection<ToggleFilterConfig>(id);
        const updatedFilter = { ...filter, option: { active } };
        setFilters((f) => {
            return {
                ...f,
                current: [...f.current].map((curr) => (curr.id === id ? updatedFilter : curr)),
            };
        });
    }

    function getActiveFilterCount(sectionValue: string): number {
        const section = getFilterSection<FilterConfig>(sectionValue);
        if (!section) {
            return 0;
        }
        if (section.type === 'checkbox' || section.type === 'select') {
            return section.options.filter((option) => option.active).length;
        }

        if (section.type === 'numeric') {
            return _isValidNumeric(section) ? 1 : 0;
        }

        if (section.type === 'date') {
            return _isValidDate(section) ? 1 : 0;
        }
        if (section.type === 'toggle') {
            return section.option.active ? 1 : 0;
        }

        return 0;
    }

    function reset(): void {
        setFilters((f) => {
            return { ...f, current: [...f.prev] };
        });
    }

    function apply(): void {
        const updatedPrevious = filters.prev.map((prev) => {
            if (prev.type === 'checkbox' || prev.type === 'select' || prev.type === 'toggle') {
                return filters.current.find((curr) => curr.id === prev.id);
            }
            if (prev.type === 'numeric' || prev.type === 'date') {
                const current = filters.current.find((curr) => curr.id === prev.id) as NumericFilterConfig | DateFilterConfig;

                if (current.option.condition !== 'between') {
                    return current;
                }
                if (current.option.base !== undefined && current.option.to !== undefined) {
                    return current;
                }
                return {
                    ...current,
                    option: { condition: '=', base: undefined, to: undefined },
                } as NumericFilterConfig | DateFilterConfig;
            }

            return prev;
        });

        setFilters((f) => {
            return { ...f, prev: updatedPrevious, current: [...f.current], queryString: buildActiveFiltersQueryString([...f.current]) };
        });
    }

    function clear(): void {
        const cleared: FilterConfig[] = filters.current.map((filter) => {
            if (filter.type === 'checkbox' || filter.type === 'select') {
                return { ...filter, options: filter.options.map((option) => ({ ...option, active: false })) };
            }
            if (filter.type === 'toggle') {
                return { ...filter, option: { active: false } };
            }
            return { ...filter, option: { base: undefined, condition: '=', to: undefined } };
        });

        setFilters((f) => {
            return { ...f, prev: cleared, current: cleared, queryString: buildActiveFiltersQueryString(cleared) };
        });
        setAllClosed(true);
    }

    function resetAllClosed(): void {
        setAllClosed(false);
    }

    /**
     * Calculates the number of sections that have active filters
     * An "active" section requires at lease one of its filters to be active e.g. a checkbox section needs at least one checkbox to be checked
     * @param filters
     * @returns
     */
    function calcActiveFilterSections(filters: Filters): number {
        let total = 0;

        for (const filter of filters) {
            if (filter.type === 'checkbox' || filter.type === 'select') {
                total += filter.options.some((option) => option.active) ? 1 : 0;
            }
            if (filter.type === 'numeric') {
                total += _isValidNumeric(filter) ? 1 : 0;
            }
            if (filter.type === 'date') {
                total += _isValidDate(filter) ? 1 : 0;
            }
            if (filter.type === 'toggle') {
                total += filter.option.active ? 1 : 0;
            }
        }

        return total;
    }

    function _isValidNumeric(section: NumericFilterConfig): boolean {
        if (section.option.condition === 'between') {
            return section.option.base !== undefined && section.option.to !== undefined;
        }

        return section.option.condition && section.option.base !== undefined;
    }

    function _isValidDate(section: DateFilterConfig): boolean {
        const { condition, base, to } = section.option;
        if (condition === 'between') {
            return (
                base !== undefined &&
                new Date(base).toString() !== 'Invalid Date' &&
                to !== undefined &&
                new Date(to).toString() !== 'Invalid Date'
            );
        }

        return section.option.condition && base !== undefined && new Date(base).toString() !== 'Invalid Date';
    }

    function buildQueryString(filters: Filters): string {
        const qs = new URLSearchParams();

        for (const filter of filters) {
            if (filter.type === 'checkbox' || filter.type === 'select') {
                const activeOptions = filter.options.filter((o) => o.active);
                if (activeOptions.length > 0) {
                    activeOptions.forEach((option) => {
                        qs.append(filter.id, option.value);
                    });
                }
            }
            if (filter.type === 'toggle' && filter.option.active) {
                qs.append(filter.id, 'true');
            }
            if (filter.type === 'numeric' && _isValidNumeric(filter)) {
                if (filter.option.condition === '=') {
                    qs.append(`${filter.id}[eq]`, filter.option.base.toString());
                }
                if (filter.option.condition === '<') {
                    qs.append(`${filter.id}[lt]`, filter.option.base.toString());
                }
                if (filter.option.condition === '>') {
                    qs.append(`${filter.id}[gt]`, filter.option.base.toString());
                }
                if (filter.option.condition === 'between') {
                    qs.append(`${filter.id}[gt]`, filter.option.base.toString());
                    qs.append(`${filter.id}[lt]`, filter.option.to.toString());
                }
                qs.append(`${filter.id}[percent]`, filter.isPercent.toString());
            }

            if (filter.type === 'date' && _isValidDate(filter)) {
                if (filter.option.condition === '=') {
                    qs.append(`${filter.id}[eq]`, filter.option.base.toString());
                }
                if (filter.option.condition === '<') {
                    qs.append(`${filter.id}[lt]`, filter.option.base.toString());
                }
                if (filter.option.condition === '>') {
                    qs.append(`${filter.id}[gt]`, filter.option.base.toString());
                }
                if (filter.option.condition === 'between') {
                    qs.append(`${filter.id}[gt]`, filter.option.base.toString());
                    qs.append(`${filter.id}[lt]`, filter.option.to.toString());
                }
            }
        }

        return qs.toString();
    }

    function buildActiveFiltersQueryString(filters: Filters): string {
        if (enableV2Query) {
            return buildQueryString(filters);
        }

        const query: { [key: string]: string[] | boolean | DateOption | (NumericOption & { isPercent: boolean }) } = {};

        for (const filter of filters) {
            if (filter.type === 'checkbox' || filter.type === 'select') {
                const activeOptions = filter.options.filter((o) => o.active);
                if (activeOptions.length > 0) {
                    query[filter.id] = activeOptions.map((option) => option.value);
                }
            }
            if (filter.type === 'toggle' && filter.option.active) {
                query[filter.id] = true;
            }
            if (filter.type === 'numeric' && _isValidNumeric(filter)) {
                query[filter.id] = { ...filter.option, isPercent: filter.isPercent };
            }

            if (filter.type === 'date' && _isValidDate(filter)) {
                query[filter.id] = { ...filter.option };
            }
        }
        if (Object.keys(query).length === 0) {
            return '';
        }
        const search = encodeURIComponent(JSON.stringify(query));
        return search;
    }

    return {
        activeSectionCount,
        getFilterSection,
        getActiveFilterCount,
        updateCheckboxOption,
        updateNumericOption,
        updateDateOption,
        updateSelectOption,
        updateToggleOption,
        reset,
        apply,
        clear,
        allClosed,
        resetAllClosed,
        result: { queryString: filters.queryString, currentFilterState: filters.current },
    };
}
